import type { CSSProperties, ReactNode } from 'react';
import { Button } from '@paige/ui';
import { useFormat, type Formatter } from '@paige/format';
import { partName } from '../pools/pools-words';
import {
  minuteIndexAt,
  minuteWords,
  positionOf,
  totalsOf,
  type FlowHistory,
  type FlowMinute,
} from './flow-history';

export interface FlowScrubberProps {
  history: FlowHistory | null;
  failed: string | null;
  /** The minute shown, by index; null is now. */
  at: number | null;
  onAt: (index: number | null) => void;
  onClose: () => void;
}

const BAR_KINDS = ['stuck', 'retrying', 'held', 'flowing'] as const;

/**
 * A minute's segments, bottom up, as shares of the chart: the bar's height is everything there (square-root scaled,
 * so one stuck item still shows beside a thousand held), split by count into needs us, retrying, held, in flight.
 */
export function segmentsOf(minute: FlowMinute, max: number) {
  const t = totalsOf(minute);
  const counts = {
    stuck: t.stuck,
    retrying: t.retrying,
    held: t.held,
    flowing: t.waiting + t.working,
  };
  const total = BAR_KINDS.reduce((n, kind) => n + counts[kind], 0);
  const bar = total > 0 ? (Math.sqrt(total) / Math.sqrt(Math.max(max, total))) * 100 : 0;
  const shares = BAR_KINDS.map((kind) => (total > 0 ? (counts[kind] / total) * bar : 0));
  return BAR_KINDS.map((kind, i) => ({
    kind,
    count: counts[kind],
    bottom: shares.slice(0, i).reduce((n, h) => n + h, 0),
    height: Math.max(counts[kind] > 0 ? 3 : 0, shares[i]!),
  })).filter((seg) => seg.count > 0);
}

/** A minute's bar: its height is everything there, split into needs us, retrying, held and in flight. */
function Bar(props: { minute: FlowMinute; max: number; x: number; chosen: boolean }): ReactNode {
  return (
    <span
      className={props.chosen ? 'fl-scrub__bar fl-scrub__bar--at' : 'fl-scrub__bar'}
      style={{ '--fl-x': `${(props.x * 100).toFixed(2)}%` } as CSSProperties}
    >
      {segmentsOf(props.minute, props.max).map((seg) => (
        <i
          key={seg.kind}
          className={`fl-scrub__seg fl-scrub__seg--${seg.kind}`}
          style={
            {
              '--fl-b': `${seg.bottom.toFixed(1)}%`,
              '--fl-h': `${seg.height.toFixed(1)}%`,
            } as CSSProperties
          }
        />
      ))}
    </span>
  );
}

interface Mark {
  key: string;
  kind: string;
  at: string;
  words: string;
}

/** The hour's releases and alerts as marks, oldest first; an alert opened before the hour starts at its edge. */
function marksOf(history: FlowHistory, f: Formatter): Mark[] {
  const start = history.minutes[0]?.at ?? history.asOf;
  return [
    ...history.releases.map((r) => ({
      key: `r-${r.at}`,
      kind: 'release',
      at: r.at,
      words: `Release ${r.build ?? 'settings'} (${r.parts.map(partName).join(', ')})`,
    })),
    ...history.alerts.map((a) => ({
      key: `a-${a.key}`,
      kind: `alert fl-scrub__mark--${a.severity}`,
      at: a.openedAt < start ? start : a.openedAt,
      words: `Alert: ${a.title}${a.closedAt ? `, cleared ${f.time(a.closedAt)}` : ', still open'}`,
    })),
  ].sort((x, y) => x.at.localeCompare(y.at));
}

/** Releases and alerts on the hour: a button each, to go to its minute. */
function Markers({
  history,
  onAt,
}: {
  history: FlowHistory;
  onAt: (i: number | null) => void;
}): ReactNode {
  const f = useFormat();
  const marks = marksOf(history, f);
  if (marks.length === 0)
    return <p className="fl-scrub__none">No release and no alert in this hour.</p>;
  return (
    <ul className="fl-scrub__marks" aria-label="Releases and alerts this hour">
      {marks.map((m) => (
        <li key={m.key}>
          <button
            type="button"
            className={`fl-scrub__mark fl-scrub__mark--${m.kind}`}
            onClick={() => onAt(minuteIndexAt(history, Date.parse(m.at)) ?? 0)}
          >
            <span className="fl-scrub__when">{f.time(m.at)}</span> {m.words}
          </button>
        </li>
      ))}
    </ul>
  );
}

/** The marks drawn on the chart itself: a line a release, a band an alert open. */
function Lines({ history }: { history: FlowHistory }): ReactNode {
  return (
    <>
      {history.releases.map((r) => (
        <i
          key={r.at}
          className="fl-scrub__line fl-scrub__line--release"
          style={{ '--fl-x': `${(positionOf(history, r.at) * 100).toFixed(2)}%` } as CSSProperties}
          title={`Release ${r.build ?? 'settings'}`}
        />
      ))}
      {history.alerts.map((a) => {
        const from = positionOf(history, a.openedAt);
        const to = a.closedAt ? positionOf(history, a.closedAt) : 1;
        return (
          <i
            key={a.key}
            className={`fl-scrub__band fl-scrub__band--${a.severity}`}
            style={
              {
                '--fl-x': `${(from * 100).toFixed(2)}%`,
                '--fl-w': `${Math.max(0.6, (to - from) * 100).toFixed(2)}%`,
              } as CSSProperties
            }
            title={a.title}
          />
        );
      })}
    </>
  );
}

function Head(props: FlowScrubberProps & { minute: FlowMinute | null }): ReactNode {
  const f = useFormat();
  const { history, minute, onAt, onClose } = props;
  return (
    <div className="fl-scrub__head">
      <strong className="fl-scrub__title">
        {minute ? `As it was at ${f.time(minute.at)}` : 'The last hour'}
      </strong>
      <span className="fl-scrub__sub">
        {history
          ? `${f.int(history.minutes.length)} recorded minutes, every company${history.testsHidden ? ', test companies left out' : ''}`
          : props.failed
            ? `Could not read the last hour: ${props.failed}`
            : 'Reading the last hour'}
      </span>
      {minute ? (
        <Button size="sm" variant="secondary" onClick={() => onAt(null)}>
          Back to now
        </Button>
      ) : null}
      <Button size="sm" variant="ghost" onClick={onClose}>
        Close
      </Button>
    </div>
  );
}

/** The slider over the hour; at its end is now. */
function Slider({
  history,
  at,
  onAt,
}: {
  history: FlowHistory;
  at: number | null;
  onAt: (i: number | null) => void;
}): ReactNode {
  const f = useFormat();
  const last = history.minutes.length - 1;
  return (
    <input
      className="fl-scrub__range"
      type="range"
      min={0}
      max={last}
      step={1}
      value={at ?? last}
      aria-label="Minute shown"
      aria-valuetext={minuteWords(history.minutes[at ?? last]!, f.time)}
      onChange={(e) => {
        const i = Number(e.currentTarget.value);
        onAt(i >= last ? null : i);
      }}
    />
  );
}

/** The hour's bars with its releases and alerts, the slider under them, and a button a marker. */
function Track({
  history,
  at,
  onAt,
}: {
  history: FlowHistory;
  at: number | null;
  onAt: (i: number | null) => void;
}): ReactNode {
  const max = Math.max(
    1,
    ...history.minutes.map((m) => Object.values(totalsOf(m)).reduce((n, v) => n + v, 0)),
  );
  return (
    <>
      <div className="fl-scrub__chart" aria-hidden="true">
        {history.minutes.map((m, i) => (
          <Bar key={m.at} minute={m} max={max} x={positionOf(history, m.at)} chosen={i === at} />
        ))}
        <Lines history={history} />
      </div>
      <Slider history={history} at={at} onAt={onAt} />
      <Markers history={history} onAt={onAt} />
    </>
  );
}

/**
 * The last hour, replayed a recorded minute at a time (paige-watch writes one a minute; 24 hours are kept): drag
 * to a minute and the picture above shows what was where then; releases and alerts sit on the track, so "when
 * did this start?" is one drag. Nothing moves here; the bars are counts.
 */
export function FlowScrubber(props: FlowScrubberProps): ReactNode {
  const { history, at, onAt } = props;
  const minute = at !== null ? (history?.minutes[at] ?? null) : null;
  return (
    <section className={minute ? 'fl-scrub fl-scrub--past' : 'fl-scrub'} aria-label="The last hour">
      <Head {...props} minute={minute} />
      {history && history.minutes.length === 0 ? (
        <p className="fl-scrub__none">
          No minute recorded yet: the health monitor writes one a minute.
        </p>
      ) : null}
      {history && history.minutes.length > 0 ? (
        <Track history={history} at={at} onAt={onAt} />
      ) : null}
    </section>
  );
}
